'use client'

import { useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { useAction } from 'next-safe-action/hooks'
import { useState } from 'react'
import { UseFormReturn } from 'react-hook-form'

import { signIn, TSignIn } from '@/entities/auth'
import { AUTH } from '@/shared/api'
import { useServerErrorHandler } from '@/shared/config/action'
import {
  setStorage,
  STORAGE_KEY,
  useSession,
  signInWithToken,
} from '@/shared/lib'

export const useSignIn = (form: UseFormReturn<TSignIn>) => {
  const session = useSession()
  const [isLoading, setIsLoading] = useState(false)

  const searchParams = useSearchParams()
  const redirect = searchParams?.get('redirect')

  const { handleServerError } = useServerErrorHandler(form)

  const t = useTranslations('login')

  const action = useAction(signIn, {
    onSuccess: async ({ data }) => {
      const { blocked, firebaseToken } = data ?? {}

      if (blocked) {
        setStorage(STORAGE_KEY.SHOW_BLOCKED_USER_DIALOG, 'true')
      }

      await session.update()

      if (firebaseToken) {
        try {
          await signInWithToken(firebaseToken)
        } catch {
          // Firebase 실패 — 로그는 signInWithToken 내부에서 처리
          // JWT 로그인은 정상 완료, 채팅 페이지에서 ChatAuthGate가 재시도
        }
      }

      if (redirect) {
        window.location.href = redirect
      } else {
        window.location.reload()
      }
    },
    onError: ({ error }) => {
      setIsLoading(false)

      const { serverError } = error

      const isInvalidCredentials = serverError?.code === AUTH['001']
      const invalidCredentialsMessage = t('password_error_invalid_credentials')

      if (isInvalidCredentials) {
        handleServerError({
          ...serverError,
          data: {
            password: invalidCredentialsMessage,
          },
        })
      } else {
        handleServerError(serverError)
      }
    },
  })

  const execute = (data: TSignIn) => {
    setIsLoading(true)
    action.execute(data)
  }

  return { ...action, execute, isPending: isLoading }
}
